import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Flame, Radio, Grid3x3, CircleDot } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const FourSeals = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].seals;
  const config = useConfig();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const seals = [
    {
      ...t.seal1,
      icon: Flame,
      image: config.images.seal1,
      gradient: 'from-primary to-secondary',
    },
    {
      ...t.seal2,
      icon: Radio,
      image: config.images.seal2,
      gradient: 'from-accent to-primary',
    },
    {
      ...t.seal3,
      icon: Grid3x3,
      image: config.images.seal3,
      gradient: 'from-muted to-foreground',
    },
    {
      ...t.seal4,
      icon: CircleDot,
      image: config.images.seal4,
      gradient: 'from-foreground/20 to-background',
    },
  ];

  return (
    <section
      id="seals"
      ref={sectionRef}
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl animate-pulse-glow" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-accent text-sm font-semibold tracking-widest uppercase">{t.subtitle}</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6">
            {t.title.split('Four Seals')[0]}<span className="text-glow-divine" style={{ color: 'hsl(42 50% 52%)' }}>{language === 'en' ? 'Four Seals' : 'Cuatro Sellos'}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.description}
          </p>
        </div>

        {/* Seals Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {seals.map((seal, index) => {
            const Icon = seal.icon;
            return (
              <Card
                key={seal.number}
                className={`group overflow-hidden bg-card/30 backdrop-blur border-border/50 hover:border-primary/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{
                  animationDelay: `${index * 150}ms`,
                }}
              >
                {/* Square Album Cover Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={seal.image}
                    alt={seal.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${seal.gradient} opacity-30 group-hover:opacity-40 transition-opacity`} />
                  
                  {/* Seal Number Overlay */}
                  <div className="absolute top-4 left-4">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${seal.gradient} flex items-center justify-center ${seal.number === 'I' ? 'divine-glow' : seal.number === 'II' ? 'soul-glow' : ''}`}>
                      <Icon className="h-8 w-8 text-background" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  {/* Seal Number & Title */}
                  <div className="mb-4">
                    <div className="text-3xl font-bold tracking-wider mb-2" style={{ fontVariantNumeric: 'oldstyle-nums' }}>
                      SEAL {seal.number}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4 tracking-wide" style={{ color: seal.number === 'I' ? 'hsl(42 50% 52%)' : seal.number === 'II' ? 'hsl(186 100% 42%)' : 'inherit' }}>
                    {seal.title}
                  </h3>

                  {/* Quote */}
                  <blockquote className="text-lg italic text-foreground/90 mb-6 border-l-2 border-primary/50 pl-4">
                    "{seal.quote}"
                  </blockquote>

                  {/* Details */}
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t.labels.theme}:</span>{' '}
                      <span className="text-foreground/90">{seal.theme}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t.labels.sound}:</span>{' '}
                      <span className="text-foreground/90">{seal.sound}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t.labels.palette}:</span>{' '}
                      <span className="text-foreground/90">{seal.palette}</span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FourSeals;
